import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Tip } from './ui/tip';

interface CouponInputProps {
  onCouponApplied: (coupon: any) => void;
  onCouponRemoved: () => void;
  appliedCoupon?: any;
  totalAmount: number;
  items: any[];
  clientId?: string;
}

const CouponInput: React.FC<CouponInputProps> = ({
  onCouponApplied,
  onCouponRemoved,
  appliedCoupon,
  totalAmount,
  items,
  clientId
}) => {
  const [couponCode, setCouponCode] = useState('');
  const [loading, setLoading] = useState(false);

  // Recalcular descuento cuando cambian los items o el total
  useEffect(() => {
    if (!appliedCoupon?.coupon) return;

    const coupon = appliedCoupon.coupon;
    let applicableAmount = totalAmount;

    if (coupon.applies_to === 'products') {
      applicableAmount = items
        .filter((i: any) => i.type === 'product' || i.type === 'producto')
        .reduce((sum: number, i: any) => sum + (i.totalPrice || 0), 0);
    } else if (coupon.applies_to === 'services') {
      applicableAmount = items
        .filter((i: any) => i.type === 'service' || i.type === 'servicio')
        .reduce((sum: number, i: any) => sum + (i.totalPrice || 0), 0);
    }

    let discountAmount = 0;
    if (coupon.discount_type === 'percentage') {
      discountAmount = (applicableAmount * coupon.discount_value) / 100;
      if (coupon.max_discount) discountAmount = Math.min(discountAmount, coupon.max_discount);
    } else {
      discountAmount = Math.min(coupon.discount_value, applicableAmount);
    }

    // Solo actualizar si cambió el monto
    if (Math.abs(discountAmount - (appliedCoupon.discount_amount || 0)) > 0.01) {
      onCouponApplied({
        ...appliedCoupon,
        discount_amount: discountAmount,
        applicable_amount: applicableAmount,
        final_amount: totalAmount - discountAmount
      });
    }
  }, [items, totalAmount]);

  const validateCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Ingresa un código de cupón');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post('/api/coupons/validate', {
        code: couponCode.toUpperCase(),
        client_id: clientId,
        items,
        total_amount: totalAmount
      });

      if (response.data.valid) {
        onCouponApplied(response.data);
        setCouponCode('');
        toast.success(`Cupón aplicado: ${response.data.coupon.name}`);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al validar el cupón');
    } finally {
      setLoading(false);
    }
  };

  const removeCoupon = () => {
    onCouponRemoved();
    setCouponCode('');
    toast.success('Cupón removido');
  };

  if (appliedCoupon) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-2">
              <span className="text-gray-900 dark:text-gray-100 font-medium">Cupón aplicado:</span>
              <span className="font-mono bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-2 py-1 rounded text-sm">
                {appliedCoupon.coupon.code}
              </span>
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
              {appliedCoupon.coupon.name} - 
              {appliedCoupon.coupon.discount_type === 'percentage' 
                ? ` ${appliedCoupon.coupon.discount_value}% de descuento`
                : ` $${appliedCoupon.coupon.discount_value} de descuento`
              }
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Descuento aplicado: ${appliedCoupon.discount_amount?.toFixed(2)}
            </p>
          </div>
          <Tip label="Remover cupón">
            <button
              onClick={removeCoupon}
              className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 p-1"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </Tip>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        Cupón de Descuento
      </label>
      <div className="flex space-x-2">
        <input
          type="text"
          value={couponCode}
          onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
          placeholder="Ingresa código del cupón"
          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-gray-900 focus:border-gray-900 dark:focus:ring-gray-100 dark:focus:border-gray-100 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          style={{ textTransform: 'uppercase' }}
          disabled={loading}
        />
        <button
          onClick={validateCoupon}
          disabled={loading || !couponCode.trim()}
          className="px-4 py-2 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 rounded-lg hover:bg-gray-800 dark:hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white dark:border-gray-900"></div>
              <span>Validando...</span>
            </>
          ) : (
            <span>Aplicar</span>
          )}
        </button>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Los cupones se aplicarán automáticamente según sus condiciones
      </p>
    </div>
  );
};

export default CouponInput;
